import { Link } from "react-router-dom";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLanguage } from "../context/LanguageContext";
import { sectionPath } from "../utils/routePaths";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

const ACCENT = "#C47C2B";

const socialIconStyle = {
  width: 38,
  height: 38,
  borderRadius: "50%",
  border: "1.5px solid rgba(255,255,255,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255,255,255,0.85)",
  textDecoration: "none",
  transition: "all 0.2s",
};
const socialIconHover = (e) => {
  e.currentTarget.style.background = "rgba(255,255,255,0.18)";
  e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
};
const socialIconLeave = (e) => {
  e.currentTarget.style.background = "transparent";
  e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
};

// Highlights the first match of `pattern` inside `text` in ACCENT — used to
// keep "Svaneti" visually emphasized inside the SEO-authored headline
// without hardcoding line breaks around it. `pattern` is a regex so Georgian
// case suffixes (სვანეთში, სვანეთის, ...) still get the whole word matched.
function highlightKeyword(text, pattern, color) {
  const m = text.match(pattern);
  if (!m) return text;
  const idx = m.index;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color }}>{m[0]}</span>
      {text.slice(idx + m[0].length)}
    </>
  );
}

export default function HeroSection() {
  const settings = useSiteSettings();
  const { language, t } = useLanguage();

  const heroEyebrow = language === "ka" && settings.heroEyebrowKa ? settings.heroEyebrowKa : settings.heroEyebrow;
  const heroHeadline = t("home_intro_title");
  const heroText = t("home_intro_text");
  const keywordPattern = language === "ka" ? /სვანეთ[Ⴀ-ჿ]*/ : /Svaneti/;

  return (
    <header style={{ position: "relative", height: "calc(100svh - 80px)", minHeight: 480, overflow: "hidden" }}>

      {/* Background image */}
      <img src={optimizedImageUrl(settings.heroImageUrl, 1920)} alt="Svaneti" fetchPriority="high"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />

      {/* Gradients */}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(100deg, rgba(10,8,6,0.88) 0%, rgba(10,8,6,0.6) 45%, rgba(10,8,6,0.1) 100%)" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,8,6,0.55) 0%, transparent 50%)" }} />

      {/* Content */}
      <div className="hero-content-inner" style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 7vw", maxWidth: 900 }}>

        {/* Eyebrow */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M2 22L10 8L14 14L18 10L22 22Z" stroke={ACCENT} strokeWidth="2.2" strokeLinejoin="round" fill="none"/>
          </svg>
          <span style={{ fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: ACCENT }}>
            {heroEyebrow || (language === "ka" ? "საქართველოს ველური გული" : "The Wild Heart of Georgia")}
          </span>
        </div>

        {/* Title */}
        <h1 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(26px, 2.6vw, 42px)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#fff", margin: "0 0 18px" }}>
          {highlightKeyword(heroHeadline, keywordPattern, ACCENT)}
        </h1>

        {/* Subtitle */}
        <p style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(14px, 1.5vw, 17px)", color: "rgba(255,255,255,0.68)", lineHeight: 1.75, margin: "0 0 36px", maxWidth: 780 }}>
          {heroText}
        </p>

        {/* CTA buttons */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <Link to={sectionPath(language, "categories")}
            style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "#2D4A3E", color: "#fff", borderRadius: 10, padding: "14px 26px", fontFamily: "Geist, sans-serif", fontSize: 15, fontWeight: 600, textDecoration: "none", transition: "all 0.2s", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#3a5c4e"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#2D4A3E"}>
            {language === "ka" ? "ადგილების გამოკვლევა" : "Explore Places"}
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
          </Link>
          <Link to={sectionPath(language, "guide")}
            style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "rgba(255,255,255,0.1)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.35)", borderRadius: 10, padding: "14px 26px", fontFamily: "Geist, sans-serif", fontSize: 15, fontWeight: 600, textDecoration: "none", transition: "all 0.2s" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}>
            {language === "ka" ? "მოგზაურობის გეგმა" : "Plan Your Trip"}
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
          </Link>
        </div>

        {/* Social links */}
        {(settings.facebookUrl || settings.instagramUrl || settings.tiktokUrl) && (
          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            {settings.facebookUrl && (
              <a
                href={settings.facebookUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                style={socialIconStyle}
                onMouseEnter={socialIconHover}
                onMouseLeave={socialIconLeave}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
            )}
            {settings.instagramUrl && (
              <a
                href={settings.instagramUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                style={socialIconStyle}
                onMouseEnter={socialIconHover}
                onMouseLeave={socialIconLeave}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
            )}
            {settings.tiktokUrl && (
              <a
                href={settings.tiktokUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
                style={socialIconStyle}
                onMouseEnter={socialIconHover}
                onMouseLeave={socialIconLeave}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.17 8.17 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z" />
                </svg>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Scroll indicator */}
      <div style={{ position: "absolute", bottom: 100, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, zIndex: 2 }}>
        <div style={{ width: 26, height: 42, border: "1.5px solid rgba(255,255,255,0.3)", borderRadius: 999, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5px 0" }}>
          <div style={{ width: 3, height: 7, background: "rgba(255,255,255,0.6)", borderRadius: 999, animation: "scrollDot 2s infinite" }} />
        </div>
      </div>

      <style>{`
        @keyframes scrollDot {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(10px); opacity: 0; }
        }
          @media (max-width: 600px) {
          .hero-content-inner { padding: 0 24px !important; max-width: 100% !important; }
        }
      `}</style>
    </header>
  );
}
